import React, { useState, useEffect } from 'react';
import {
	FileText,
	Search,
	Filter,
	Download,
	Calendar,
	User,
	Shield,
	Activity,
	AlertTriangle,
	CheckCircle,
	XCircle,
	Eye,
	Edit3,
	Trash2,
	UserPlus,
	LogIn,
	LogOut as LogOutIcon,
	Settings,
	Clock,
	Globe,
	Hash,
} from 'lucide-react';
import Button from './ui/Button';
import { supabase } from '../supabaseClient';
import FilterDropdown from './common/FilterDropdown';
import DashboardHeader from './dashboard/DashboardHeader';

interface AuditLog {
	id: string;
	timestamp: Date;
	user_email: string;
	action: string;
	category: 'authentication' | 'user_management' | 'verification' | 'system' | 'data_access';
	status: 'success' | 'failed' | 'warning';
	details: any;
	ip_address: string;
	user_agent: string;
	request_method?: string;
	request_path?: string;
	duration_ms?: number;
	created_at: Date;
}

const Logs: React.FC = () => {
	const [logs, setLogs] = useState<AuditLog[]>([]);
	const [loading, setLoading] = useState<boolean>(true);
	const [filteredLogs, setFilteredLogs] = useState<AuditLog[]>([]);
	const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null);
	const [searchTerm, setSearchTerm] = useState('');
	const [categoryFilter, setCategoryFilter] = useState('all');
	const [statusFilter, setStatusFilter] = useState('all');
	const [dateFilter, setDateFilter] = useState('all');
	const [currentPage, setCurrentPage] = useState(1);
	const [userEmails, setUserEmails] = useState<{ [userId: string]: string }>({});
	const ITEMS_PER_PAGE = 15;

	useEffect(() => {
		// Load initial logs from Supabase
		const fetchLogs = async () => {
			setLoading(true);
			try {
				const { data: profile } = await supabase.auth.getUser();
				if (!profile.user) {
					console.error('No authenticated user');
					setLoading(false);
					return;
				}

				console.log('Fetching logs for user:', profile.user.email);

				const { data, error } = await supabase
					.from('audit_logs')
					.select('*')
					.order('created_at', { ascending: false });

				if (error) {
					console.error('Error fetching logs:', error.message);
					setLogs([]);
				} else if (!data) {
					console.log('No logs data returned');
					setLogs([]);
				} else {
					console.log('Fetched logs:', data.length);
					// Parse string timestamp into Date objects if necessary
					const parsed = data.map(log => ({
						...log,
						timestamp: new Date(log.timestamp || log.created_at),
						created_at: new Date(log.created_at),
						user_email: log.user_email || 'system',
						details: typeof log.details === 'string' ? JSON.parse(log.details) : log.details || {},
						category: log.category || 'system',
						status: log.status || 'success',
					}));
					console.log('Parsed logs:', parsed);
					setLogs(parsed);
				}
			} catch (err) {
				console.error('Error in fetchLogs:', err);
				setLogs([]);
			}
			setLoading(false);
		};

		// Initial data load
		fetchLogs();

		// Set up real-time subscription to audit_logs table
		const subscription = supabase
			.channel('audit_logs_changes')
			.on('postgres_changes', { event: '*', schema: 'public', table: 'audit_logs' }, payload => {
				console.log('Received real-time update:', payload.eventType);
				const { eventType, new: newRecord, old: oldRecord } = payload;

				switch (eventType) {
					case 'INSERT':
						// Add the new log entry to the top of the list
						if (newRecord) {
							console.log('Processing new log:', newRecord);
							const parsedLog: AuditLog = {
								...newRecord,
								timestamp: new Date(newRecord.timestamp || newRecord.created_at),
								created_at: new Date(newRecord.created_at),
								user_email: newRecord.user_email || 'system',
								details:
									typeof newRecord.details === 'string'
										? JSON.parse(newRecord.details)
										: newRecord.details || {},
								category: newRecord.category || 'system',
								status: newRecord.status || 'success',
							};
							setLogs(prev => [parsedLog, ...prev]);
						}
						break;
					case 'UPDATE':
						// Update the existing log entry
						if (newRecord) {
							console.log('Processing updated log:', newRecord);
							const parsedLog: AuditLog = {
								...newRecord,
								timestamp: new Date(newRecord.timestamp || newRecord.created_at),
								created_at: new Date(newRecord.created_at),
								user_email: newRecord.user_email || 'system',
								details:
									typeof newRecord.details === 'string'
										? JSON.parse(newRecord.details)
										: newRecord.details || {},
								category: newRecord.category || 'system',
								status: newRecord.status || 'success',
							};
							setLogs(prev => prev.map(log => (log.id === parsedLog.id ? parsedLog : log)));
						}
						break;
					case 'DELETE':
						// Remove the log entry
						if (oldRecord && oldRecord.id) {
							console.log('Processing deleted log:', oldRecord.id);
							setLogs(prev => prev.filter(log => log.id !== oldRecord.id));
						}
						break;
				}
			})
			.subscribe();

		// Cleanup subscription on component unmount
		return () => {
			supabase.removeChannel(subscription);
		};
	}, []);

	useEffect(() => {
		console.log('Filtering logs:', logs.length);
		let filtered = [...logs]; // Create a new array to avoid mutation

		if (searchTerm) {
			console.log('Applying search filter:', searchTerm);
			filtered = filtered.filter(
				log =>
					(log.user_email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
					(log.action || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
					(log.request_path || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
					JSON.stringify(log.details || {})
						.toLowerCase()
						.includes(searchTerm.toLowerCase())
			);
		}

		if (categoryFilter !== 'all') {
			console.log('Applying category filter:', categoryFilter);
			filtered = filtered.filter(log => log.category === categoryFilter);
		}

		if (statusFilter !== 'all') {
			console.log('Applying status filter:', statusFilter);
			filtered = filtered.filter(log => log.status === statusFilter);
		}

		if (dateFilter !== 'all') {
			console.log('Applying date filter:', dateFilter);
			const now = new Date();
			const filterDate = new Date();
			switch (dateFilter) {
				case 'today':
					filterDate.setHours(0, 0, 0, 0);
					filtered = filtered.filter(log => log.created_at >= filterDate);
					break;
				case 'week':
					filterDate.setDate(now.getDate() - 7);
					filtered = filtered.filter(log => log.created_at >= filterDate);
					break;
				case 'month':
					filterDate.setMonth(now.getMonth() - 1);
					filtered = filtered.filter(log => log.created_at >= filterDate);
					break;
			}
		}

		console.log('Filtered logs:', filtered.length);
		setFilteredLogs(filtered);
	}, [logs, searchTerm, categoryFilter, statusFilter, dateFilter]);

	useEffect(() => {
		setCurrentPage(1);
	}, [searchTerm, categoryFilter, statusFilter, dateFilter]);

	const paginatedLogs = filteredLogs.slice(
		(currentPage - 1) * ITEMS_PER_PAGE,
		currentPage * ITEMS_PER_PAGE
	);

	const stats = {
		total: logs.length,
		success: logs.filter(l => l.status === 'success').length,
		failed: logs.filter(l => l.status === 'failed').length,
		warning: logs.filter(l => l.status === 'warning').length,
	};

	const formatDate = (date: Date) =>
		new Intl.DateTimeFormat('en-GB', {
			day: '2-digit',
			month: 'short',
			year: 'numeric',
			hour: '2-digit',
			minute: '2-digit',
			second: '2-digit',
		}).format(date);

	const getStatusIcon = (status: string) => {
		switch (status) {
			case 'success':
				return <CheckCircle className="w-3 h-3 text-green-600" />;
			case 'failed':
				return <XCircle className="w-3 h-3 text-red-600" />;
			case 'warning':
				return <AlertTriangle className="w-3 h-3 text-yellow-600" />;
			default:
				return <Activity className="w-3 h-3 text-gray-600" />;
		}
	};

	const getStatusBadge = (status: string) => {
		const base = 'px-1 py-1 rounded-full text-xs font-medium';
		if (status === 'success') return `${base} bg-green-100 text-green-800`;
		if (status === 'failed') return `${base} bg-red-100 text-red-800`;
		if (status === 'warning') return `${base} bg-yellow-100 text-yellow-800`;
		return `${base} bg-gray-100 text-gray-800`;
	};

	const getCategoryIcon = (category: string) => {
		switch (category) {
			case 'authentication':
				return <LogOutIcon className="w-3 h-3 text-blue-600" />;
			case 'user_management':
				return <UserPlus className="w-3 h-3 text-purple-600" />;
			case 'verification':
				return <Shield className="w-3 h-3 text-green-600" />;
			case 'system':
				return <Settings className="w-3 h-3 text-gray-600" />;
			case 'data_access':
				return <Eye className="w-3 h-3 text-orange-600" />;
			default:
				return <Activity className="w-3 h-3 text-gray-600" />;
		}
	};

	const getCategoryBadge = (category: string) => {
		const base = 'px-1 py-1 rounded-full text-xs font-medium';
		switch (category) {
			case 'authentication':
				return `${base}text-sm bg-blue-100 text-blue-800`;
			case 'user_management':
				return `${base}text-sm bg-purple-100 text-purple-800`;
			case 'verification':
				return `${base}text-sm bg-green-100 text-green-800`;
			case 'system':
				return `${base}text-sm bg-gray-100 text-gray-800`;
			case 'data_access':
				return `${base}text-sm bg-orange-100 text-orange-800`;
			default:
				return `${base}text-sm bg-gray-100 text-gray-800`; // fallback for unknown/null
		}
	};

	const clearFilters = () => {
		setSearchTerm('');
		setCategoryFilter('all');
		setStatusFilter('all');
		setDateFilter('all');
	};

	const exportLogs = () => {
		const csvContent = [
			'Timestamp,User,Action,Category,Status,Duration (ms),IP Address,Request Method,Request Path',
			...filteredLogs.map(
				log =>
					`${log.timestamp.toISOString()},${log.user_email},${log.action},${log.category},${
						log.status
					},${log.duration_ms || ''},${log.ip_address},${log.request_method || ''},${
						log.request_path || ''
					}`
			),
		].join('\n');

		const blob = new Blob([csvContent], { type: 'text/csv' });
		const url = window.URL.createObjectURL(blob);
		const a = document.createElement('a');
		a.href = url;
		a.download = `audit-logs-${new Date().toISOString().split('T')[0]}.csv`;
		a.click();
		window.URL.revokeObjectURL(url);
	};

	return (
		<div className="container mx-auto px-4 py-8">
			{/* Header */}
			<DashboardHeader
				title="Audit Logs"
				description="Track all system activities and user actions"
				buttonLabel="Export Logs"
				buttonPermission="logs.read"
				onButtonAction={exportLogs}
			/>

			{/* Filters */}
			<div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 mb-8 mt-8">
				<div className="flex flex-wrap gap-4 items-center">
					<div className="relative flex-1 min-w-[200px]">
						<Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-3 h-3 text-gray-400" />
						<input
							type="text"
							value={searchTerm}
							onChange={e => setSearchTerm(e.target.value)}
							placeholder="Search logs by user, action, or resource..."
							className="w-full text-sm pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
						/>
					</div>
					<FilterDropdown
						triggerLabel="Category"
						selectedValue={categoryFilter}
						placeholder="All Categories"
						options={[
							{ value: 'all', label: 'All Categories' },
							{ value: 'authentication', label: 'Authentication' },
							{ value: 'user_management', label: 'User Management' },
							{ value: 'verification', label: 'Verification' },
							{ value: 'system', label: 'System' },
							{ value: 'data_access', label: 'Data Access' },
						]}
						onChange={setCategoryFilter}
					/>
					<FilterDropdown
						triggerLabel="Status"
						selectedValue={statusFilter}
						placeholder="All Status"
						options={[
							{ value: 'all', label: 'All Status' },
							{ value: 'success', label: 'Success' },
							{ value: 'failed', label: 'Failed' },
							{ value: 'warning', label: 'Warning' },
						]}
						onChange={setStatusFilter}
					/>
					<FilterDropdown
						triggerLabel="Date Range"
						selectedValue={dateFilter}
						placeholder="All Time"
						options={[
							{ value: 'all', label: 'All Time' },
							{ value: 'today', label: 'Today' },
							{ value: 'week', label: 'This Week' },
							{ value: 'month', label: 'This Month' },
						]}
						onChange={setDateFilter}
					/>
					{(searchTerm ||
						categoryFilter !== 'all' ||
						statusFilter !== 'all' ||
						dateFilter !== 'all') && (
						<button
							onClick={clearFilters}
							className="text-red-600 hover:text-red-700 font-medium text-sm"
						>
							Clear Filters
						</button>
					)}
				</div>
			</div>

			{/* Logs Table */}
			<div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
				<div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-x-auto">
					<table className="min-w-full divide-y divide-gray-200 text-xs sm:text-sm">
						<thead className="bg-gray-50">
							<tr>
								<th className="px-2 sm:px-4 py-2 sm:py-4 text-left font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
									Timestamp
								</th>
								<th className="px-2 sm:px-4 py-2 sm:py-4 text-left font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
									User
								</th>
								<th className="px-2 sm:px-4 py-2 sm:py-4 text-left font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
									Action
								</th>
								<th className="px-2 sm:px-4 py-2 sm:py-4 text-left font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
									Category
								</th>
								<th className="px-2 sm:px-4 py-2 sm:py-4 text-left font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
									Status
								</th>
								<th className="px-2 sm:px-4 py-2 sm:py-4 text-left font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
									Duration
								</th>
								<th className="px-2 sm:px-4 py-2 sm:py-4 text-left font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
									Details
								</th>
							</tr>
						</thead>
						<tbody className="bg-white divide-y divide-gray-200">
							{loading ? (
								// Skeleton loader - renders multiple skeleton rows
								Array.from({ length: 5 }).map((_, index) => (
									<tr key={`skeleton-${index}`}>
										<td className="px-2 sm:px-4 py-2 sm:py-4 whitespace-nowrap">
											<div className="h-4 bg-gray-200 rounded animate-pulse w-24"></div>
										</td>
										<td className="px-2 sm:px-4 py-2 sm:py-4 whitespace-nowrap">
											<div className="h-4 bg-gray-200 rounded animate-pulse w-24"></div>
										</td>
										<td className="px-2 sm:px-4 py-2 sm:py-4 whitespace-nowrap">
											<div className="h-4 bg-gray-200 rounded animate-pulse w-20"></div>
										</td>
										<td className="px-2 text-xs sm:px-4 py-2 sm:py-4 whitespace-nowrap">
											<div className="h-4 bg-gray-200 rounded animate-pulse w-16"></div>
										</td>
										<td className="px-2 sm:px-4 py-2 sm:py-4 whitespace-nowrap">
											<div className="h-4 bg-gray-200 rounded animate-pulse w-16"></div>
										</td>
										<td className="px-2 sm:px-4 py-2 sm:py-4 whitespace-nowrap">
											<div className="h-4 bg-gray-200 rounded animate-pulse w-16"></div>
										</td>
										<td className="px-2 text-xs sm:px-4 py-2 sm:py-4 max-w-[120px] sm:max-w-xs truncate">
											<div className="h-4 bg-gray-200 rounded animate-pulse w-16"></div>
										</td>
									</tr>
								))
							) : paginatedLogs.length === 0 ? (
								<tr>
									<td
										colSpan={7}
										className="px-2 sm:px-4 py-2 sm:py-4 text-center text-sm text-gray-500"
									>
										No logs found matching the current filters
									</td>
								</tr>
							) : (
								paginatedLogs.map(log => (
									<tr
										key={log.id}
										className="hover:bg-gray-50 cursor-pointer"
										onClick={() => setSelectedLog(log)}
									>
										<td className="px-2 sm:px-4 py-2 sm:py-4 whitespace-nowrap">
											{formatDate(log.timestamp)}
										</td>
										<td className="px-2 sm:px-4 py-2 sm:py-4 whitespace-nowrap">
											{log.user_email}
										</td>
										<td className="px-2 sm:px-4 py-2 sm:py-4 whitespace-nowrap">{log.action}</td>
										<td className="px-2 text-xs sm:px-4 py-2 sm:py-4 whitespace-nowrap">
											<div className="flex items-center gap-2">
												{getCategoryIcon(log.category)}
												<span className={getCategoryBadge(log.category || 'unknown')}>
													{(log.category || 'unknown').replace('_', ' ')}
												</span>
											</div>
										</td>
										<td className="px-2 sm:px-4 py-2 sm:py-4 whitespace-nowrap">
											<div className="flex items-center gap-2">
												{getStatusIcon(log.status)}
												<span className={getStatusBadge(log.status)}>{log.status}</span>
											</div>
										</td>
										<td className="px-2 sm:px-4 py-2 sm:py-4 whitespace-nowrap">
											{log.duration_ms ? (
												<div className="flex items-center gap-1">
													<Clock className="w-3 h-3 text-gray-400" />
													<span className="text-gray-600">{log.duration_ms}ms</span>
												</div>
											) : (
												'-'
											)}
										</td>
										<td className="px-2 text-xs sm:px-4 py-2 sm:py-4 max-w-[200px] truncate">
											{typeof log.details === 'object' ? (
												<div className="flex items-center gap-2">
													{log.details.request_id && (
														<span className="text-gray-500">
															<Hash className="w-3 h-3 inline mr-1" />
															{log.details.request_id.slice(0, 8)}
														</span>
													)}
													{log.details.submission_id && (
														<span className="text-blue-500">
															ID: {log.details.submission_id.slice(0, 8)}
														</span>
													)}
													{log.details.error && (
														<span className="text-red-500">{log.details.error}</span>
													)}
												</div>
											) : (
												log.details
											)}
										</td>
									</tr>
								))
							)}
						</tbody>
					</table>
				</div>

				{/* Pagination */}
				{filteredLogs.length > ITEMS_PER_PAGE && (
					<div className="bg-white px-6 py-4 border-t border-gray-200 flex items-center justify-between">
						<div className="text-sm text-gray-700">
							Showing {(currentPage - 1) * ITEMS_PER_PAGE + 1} to{' '}
							{Math.min(currentPage * ITEMS_PER_PAGE, filteredLogs.length)} of {filteredLogs.length}{' '}
							results
						</div>
						<div className="flex items-center gap-2">
							<button
								onClick={() => setCurrentPage(p => Math.max(p - 1, 1))}
								disabled={currentPage === 1}
								className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
							>
								Previous
							</button>
							<span className="px-3 py-2 text-sm font-medium text-gray-700">
								Page {currentPage} of {Math.ceil(filteredLogs.length / ITEMS_PER_PAGE)}
							</span>
							<button
								onClick={() =>
									setCurrentPage(p =>
										Math.min(p + 1, Math.ceil(filteredLogs.length / ITEMS_PER_PAGE))
									)
								}
								disabled={currentPage === Math.ceil(filteredLogs.length / ITEMS_PER_PAGE)}
								className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
							>
								Next
							</button>
						</div>
					</div>
				)}
			</div>

			{/* Log Detail Modal */}
			{selectedLog && (
				<div className="fixed inset-0 bg-black/50 flex justify-center items-center z-50 p-4">
					<div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-2xl relative max-h-[90vh] overflow-y-auto">
						<button
							onClick={() => setSelectedLog(null)}
							className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
						>
							<XCircle className="w-6 h-6" />
						</button>

						<h3 className="text-xl font-semibold text-gray-900 mb-6">Audit Log Details</h3>

						<div className="space-y-4">
							<div className="grid grid-cols-2 gap-4 text-sm">
								<div>
									<span className="font-medium text-gray-600">Timestamp:</span>
									<p className="text-gray-900">{formatDate(selectedLog.timestamp)}</p>
								</div>
								<div>
									<span className="font-medium text-gray-600">User:</span>
									<p className="text-gray-900">{selectedLog.user_email}</p>
								</div>
								<div>
									<span className="font-medium text-gray-600">Action:</span>
									<p className="text-gray-900">{selectedLog.action}</p>
								</div>
								<div>
									<span className="font-medium text-gray-600">Request:</span>
									<p className="text-gray-900">
										{selectedLog.request_method} {selectedLog.request_path}
									</p>
								</div>
								<div>
									<span className="font-medium text-gray-600">Category:</span>
									<div className="flex items-center gap-2 mt-1">
										{getCategoryIcon(selectedLog.category)}
										<span className={getCategoryBadge(selectedLog.category || 'unknown')}>
											{(selectedLog.category || 'unknown').replace('_', ' ')}
										</span>
									</div>
								</div>
								<div>
									<span className="font-medium text-gray-600">Status:</span>
									<div className="flex items-center gap-2 mt-1">
										{getStatusIcon(selectedLog.status)}
										<span className={getStatusBadge(selectedLog.status)}>{selectedLog.status}</span>
									</div>
								</div>
								<div>
									<span className="font-medium text-gray-600">Duration:</span>
									<p className="text-gray-900">
										{selectedLog.duration_ms ? `${selectedLog.duration_ms}ms` : 'N/A'}
									</p>
								</div>
								<div>
									<span className="font-medium text-gray-600">IP Address:</span>
									<div className="flex items-center gap-2">
										<Globe className="w-3 h-3 text-gray-400" />
										<p className="text-gray-900 font-mono">{selectedLog.ip_address}</p>
									</div>
								</div>
							</div>

							<div>
								<span className="font-medium text-gray-600">Details:</span>
								<pre className="text-gray-900 mt-1 text-xs font-mono bg-gray-50 p-3 rounded overflow-x-auto">
									{JSON.stringify(selectedLog.details, null, 2)}
								</pre>
							</div>

							<div>
								<span className="font-medium text-gray-600">User Agent:</span>
								<p className="text-gray-900 mt-1 text-xs font-mono bg-gray-50 p-2 rounded">
									{selectedLog.user_agent}
								</p>
							</div>
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default Logs;
